import type React from "react";
import { cloneElement, useCallback } from "react";
import { twJoin } from "tailwind-merge";

import type { GroupDto, UserDto } from "@/api/types";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import type { IconComponent } from "@/components/Icon/Icon";
import { User } from "@/components/User/User";
import { buttonVariants } from "@/design-system/Button";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Link } from "@/design-system/Link";
import { twResolve } from "@/helpers/tw-resolve";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface CommunityPostRootProps {
  user: UserDto;
  group?: GroupDto;
  dateSubtitle?: React.ReactNode;
  actions?: ContextMenuItem[];
  children: React.ReactNode;
  isReported?: boolean;
  isArchivedOrDeleted?: boolean;
  isUnread?: boolean;
  renderBadges?: () => React.ReactNode;
  renderHeaders?: () => React.ReactNode;
  onEnterViewport?: () => void;
  onLeaveViewport?: () => void;
}

function CommunityPostRoot({
  dateSubtitle,
  group,
  user,
  children,
  actions,
  isReported,
  isArchivedOrDeleted,
  isUnread,
  renderHeaders,
  renderBadges,
  onEnterViewport,
  onLeaveViewport,
}: CommunityPostRootProps): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();

  const refContainer = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      onEnterViewport?.();
    }, [onEnterViewport]),
    onDiverge: useCallback(() => {
      onLeaveViewport?.();
    }, [onLeaveViewport]),
  });

  let borderTone: "brand" | "danger" | "default" = "default";
  if (isUnread) {
    borderTone = "brand";
  }
  if (isReported) {
    borderTone = "danger";
  }

  return (
    <article
      className={twJoin(
        cardSurfaceVariants({ borderTone }),
        twResolve("flex flex-none flex-col", isArchivedOrDeleted && "bg-background-on-default"),
      )}
      ref={refContainer}
    >
      {renderHeaders && renderHeaders()}
      <section className="flex flex-col gap-space-sm p-space-md">
        <Inline gap="sm" align="center" wrap={false}>
          <header className="flex w-full flex-wrap items-center justify-between gap-space-md">
            <User
              user={user}
              renderSubtitle={dateSubtitle ? () => dateSubtitle : undefined}
              renderPostfix={
                group
                  ? () => (
                      <Link
                        data-testid={`community-item-${group.type}-group-anchor`}
                        href={
                          group.type === "helpCategory"
                            ? routes.helpCategories.details({ slug, id: group.id })
                            : routes.interestGroups.details({ slug, id: group.id })
                        }
                        variant="caption"
                        tone="default"
                        underline="hover"
                      >
                        {t("component.community-post.posted-in-group", { group: group.name })}
                      </Link>
                    )
                  : undefined
              }
            />
            {renderBadges && (
              <aside className="order-first flex w-full flex-wrap items-center gap-space-sm @sm:order-0 @sm:w-auto">
                {renderBadges()}
              </aside>
            )}
          </header>
          {actions && actions.length > 0 && <ContextMenu items={actions} />}
        </Inline>
        {children}
      </section>
    </article>
  );
}

type CommunityPostHeaderProps = {
  title: string;
  icon: IconComponent;
  variant?: "success" | "warning" | "danger" | "default";
  "data-testid"?: string;
  onClick?: () => void;
};

function CommunityPostHeader({
  variant = "default",
  title,
  icon,
  "data-testid": dataTestId,
  onClick,
}: CommunityPostHeaderProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <header
      className={twJoin(
        "flex w-full items-center justify-between rounded-t-lg px-space-md py-space-sm",
        variant === "success" && "bg-background-success-secondary text-text-on-success-secondary",
        variant === "warning" && "bg-background-warning-secondary text-text-on-warning-secondary",
        variant === "danger" && "bg-background-danger-secondary text-text-on-danger-secondary",
        variant === "default" && "bg-background-on-default text-text-secondary ring ring-border-default",
      )}
      data-testid={dataTestId}
    >
      <Inline gap="sm" align="center">
        {icon && cloneElement(icon, { size: 16 })}
        <span className="text-body-emphasis">{title}</span>
      </Inline>
      {onClick && (
        <button
          className={twResolve(
            buttonVariants({ variant: "ghostSecondary", size: "sm", type: "button" }),
            "text-caption hover:underline",
          )}
          type="button"
          onClick={onClick}
        >
          {t("component.community-post.header.btn.view-reach-details")}
        </button>
      )}
    </header>
  );
}

export const CommunityPost = {
  Root: CommunityPostRoot,
  Header: CommunityPostHeader,
};
